import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { fade, makeStyles, Paper, Tabs, Tab } from "@material-ui/core";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import SuperConnectionsPanel from "../../components/Settings/SuperConnectionsPanel";
import AgentTemplatesManager from "../../components/AgentTemplatesManager";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minWidth: 0,
  },
  shell: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  tabsPaper: {
    borderRadius: 20,
    border: `1px solid ${fade(theme.palette.divider, 0.86)}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
    padding: theme.spacing(0.75),
  },
  tabs: {
    minHeight: 0,
    "& .MuiTabs-flexContainer": {
      gap: theme.spacing(1),
      flexWrap: "wrap",
    },
  },
  tabsIndicator: {
    display: "none",
  },
  tab: {
    minHeight: 40,
    minWidth: 0,
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    transition: "all 160ms ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75, 1.25),
    },
  },
  panel: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(
    () => new URLSearchParams(window.location.search).get("tab") || "options"
  );
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        const companyId = user.companyId;
        const company = await find(companyId);

        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }

    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("tab", newValue);
    window.history.replaceState({}, "", nextUrl.toString());
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => currentUser.super;

  return (
    <MainContainer className={classes.root} fullWidth>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>{i18n.t("settings.title")}</Title>
          </MainHeader>

          <div className={classes.shell}>
            <Paper className={classes.tabsPaper} elevation={0}>
              <Tabs
                value={tab}
                indicatorColor="primary"
                textColor="primary"
                scrollButtons="on"
                variant="scrollable"
                onChange={handleTabChange}
                className={classes.tabs}
                classes={{ indicator: classes.tabsIndicator }}
              >
                <Tab
                  className={classes.tab}
                  label={i18n.t("settings.tabs.options")}
                  value={"options"}
                />
                {schedulesEnabled && (
                  <Tab className={classes.tab} label="Horários" value={"schedules"} />
                )}
                {isSuper() ? (
                  <Tab className={classes.tab} label="Empresas" value={"companies"} />
                ) : null}
                {isSuper() ? (
                  <Tab
                    className={classes.tab}
                    label={i18n.t("settings.tabs.plans")}
                    value={"plans"}
                  />
                ) : null}
                {isSuper() ? (
                  <Tab
                    className={classes.tab}
                    label="Templates de IA"
                    value={"agentTemplates"}
                  />
                ) : null}
                {isSuper() ? (
                  <Tab
                    className={classes.tab}
                    label={i18n.t("settings.tabs.helps")}
                    value={"helps"}
                  />
                ) : null}
                {isSuper() ? (
                  <Tab
                    className={classes.tab}
                    label={i18n.t("settings.tabs.superConnections")}
                    value={"superConnections"}
                  />
                ) : null}
              </Tabs>
            </Paper>

            <div className={classes.panel}>
              <TabPanel className={classes.panel} value={tab} name={"schedules"}>
                <SchedulesForm
                  loading={loading}
                  onSubmit={handleSubmitSchedules}
                  initialValues={schedules}
                />
              </TabPanel>

              <OnlyForSuperUser
                user={currentUser}
                yes={() => (
                  <>
                    <TabPanel className={classes.panel} value={tab} name={"companies"}>
                      <CompaniesManager />
                    </TabPanel>

                    <TabPanel className={classes.panel} value={tab} name={"plans"}>
                      <PlansManager />
                    </TabPanel>

                    <TabPanel className={classes.panel} value={tab} name={"helps"}>
                      <HelpsManager />
                    </TabPanel>

                    <TabPanel
                      className={classes.panel}
                      value={tab}
                      name={"superConnections"}
                    >
                      <SuperConnectionsPanel />
                    </TabPanel>

                    <TabPanel
                      className={classes.panel}
                      value={tab}
                      name={"agentTemplates"}
                    >
                      <AgentTemplatesManager />
                    </TabPanel>
                  </>
                )}
              />

              <TabPanel className={classes.panel} value={tab} name={"options"}>
                <Options
                  settings={settings}
                  oldSettings={oldSettings}
                  user={currentUser}
                  scheduleTypeChanged={(value) =>
                    setSchedulesEnabled(value === "company")
                  }
                />
              </TabPanel>
            </div>
          </div>
        </>
      )}
    </MainContainer>
  );
};

export default SettingsCustom;
